import { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Calendar, FileText, AlertTriangle, FlaskConical, Activity, TrendingUp } from "lucide-react";

interface LabResult {
  id: string;
  patient_id: string;
  test_name: string;
  value: number | null;
  unit: string | null;
  ref_low: number | null;
  ref_high: number | null;
  collected_at: string | null;
  created_at: string;
  subcategory_id: string | null;
  source_type?: string;
}

interface PatientStatisticsProps {
  totalContents: number;
  totalRecords: number;
  totalAlerts: number;
  totalLabResults: number;
  labResults: LabResult[];
}

export function PatientStatistics({
  totalContents,
  totalRecords,
  totalAlerts,
  totalLabResults,
  labResults,
}: PatientStatisticsProps) {
  // Calcular estatísticas dos últimos 4 cards
  const stats = useMemo(() => {
    // Filtrar apenas resultados com valor não nulo e com subcategoria
    const validResults = labResults.filter(
      (r) => r.value !== null && r.subcategory_id !== null
    );

    // Contar subcategorias únicas
    const uniqueSubcategories = new Set(
      validResults.map((r) => r.subcategory_id).filter(Boolean)
    );

    // Função para verificar se está fora da faixa de referência
    const isOutOfRange = (result: LabResult): boolean => {
      if (result.value === null) return false;
      if (result.ref_low !== null && result.value < result.ref_low) return true;
      if (result.ref_high !== null && result.value > result.ref_high) return true;
      return false;
    };

    // Contar resultados fora do normal
    const outOfRange = validResults.filter(isOutOfRange).length;

    // Contar resultados normais
    const normal = validResults.filter((r) => !isOutOfRange(r)).length;

    return {
      subcategoryCount: uniqueSubcategories.size,
      totalMeasurements: validResults.length,
      outOfRange,
      normal,
    };
  }, [labResults]);

  return (
    <div className="space-y-4 w-full max-w-full overflow-x-hidden">
      {/* Primeira linha - 4 cards */}
      <div className="grid grid-cols-1 gap-3 sm:gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card className="bg-white border border-slate-100 rounded-xl shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between pb-2 px-4 sm:px-6 pt-4 sm:pt-6">
            <CardTitle className="text-sm font-medium text-teal-600">
              Conteúdos
            </CardTitle>
            <Calendar className="h-4 w-4 sm:h-5 sm:w-5 text-teal-600 flex-shrink-0" />
          </CardHeader>
          <CardContent className="px-4 sm:px-6 pb-4 sm:pb-6">
            <div className="text-2xl sm:text-3xl font-bold text-teal-600">
              {totalContents}
            </div>
            <p className="text-xs sm:text-sm text-slate-600 mt-1">
              Sessões cadastradas
            </p>
          </CardContent>
        </Card>

        <Card className="bg-white border border-slate-100 rounded-xl shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between pb-2 px-4 sm:px-6 pt-4 sm:pt-6">
            <CardTitle className="text-sm font-medium text-teal-600">
              Registros
            </CardTitle>
            <FileText className="h-4 w-4 sm:h-5 sm:w-5 text-teal-600 flex-shrink-0" />
          </CardHeader>
          <CardContent className="px-4 sm:px-6 pb-4 sm:pb-6">
            <div className="text-2xl sm:text-3xl font-bold text-teal-600">
              {totalRecords}
            </div>
            <p className="text-xs sm:text-sm text-slate-600 mt-1">
              Registros de saúde
            </p>
          </CardContent>
        </Card>

        <Card className="bg-white border border-slate-100 rounded-xl shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between pb-2 px-4 sm:px-6 pt-4 sm:pt-6">
            <CardTitle className="text-sm font-medium text-teal-600">
              Alertas
            </CardTitle>
            <AlertTriangle className="h-4 w-4 sm:h-5 sm:w-5 text-orange-500 flex-shrink-0" />
          </CardHeader>
          <CardContent className="px-4 sm:px-6 pb-4 sm:pb-6">
            <div className="text-2xl sm:text-3xl font-bold text-teal-600">
              {totalAlerts}
            </div>
            <p className="text-xs sm:text-sm text-slate-600 mt-1">
              {totalAlerts > 0 ? "Requer atenção" : "Nenhum alerta"}
            </p>
          </CardContent>
        </Card>

        <Card className="bg-white border border-slate-100 rounded-xl shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between pb-2 px-4 sm:px-6 pt-4 sm:pt-6">
            <CardTitle className="text-sm font-medium text-teal-600">
              Resultados
            </CardTitle>
            <FlaskConical className="h-4 w-4 sm:h-5 sm:w-5 text-teal-600 flex-shrink-0" />
          </CardHeader>
          <CardContent className="px-4 sm:px-6 pb-4 sm:pb-6">
            <div className="text-2xl sm:text-3xl font-bold text-teal-600">
              {totalLabResults}
            </div>
            <p className="text-xs sm:text-sm text-slate-600 mt-1">
              Total de resultados
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Segunda linha - 4 cards */}
      <div className="grid grid-cols-1 gap-3 sm:gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card className="bg-white border border-slate-100 rounded-xl shadow-sm">
          <CardContent className="pt-4 sm:pt-6 px-4 sm:px-6 pb-4 sm:pb-6">
            <div className="flex items-center gap-1.5 sm:gap-2 mb-1 sm:mb-2">
              <FlaskConical className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-teal-600 flex-shrink-0" />
              <CardTitle className="text-xs sm:text-sm font-medium text-teal-600">
                Subcategorias medidas
              </CardTitle>
            </div>
            <div className="text-2xl sm:text-3xl font-bold text-teal-600">
              {stats.subcategoryCount}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border border-slate-100 rounded-xl shadow-sm">
          <CardContent className="pt-4 sm:pt-6 px-4 sm:px-6 pb-4 sm:pb-6">
            <div className="flex items-center gap-1.5 sm:gap-2 mb-1 sm:mb-2">
              <Activity className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-teal-600 flex-shrink-0" />
              <CardTitle className="text-xs sm:text-sm font-medium text-teal-600">
                Medições
              </CardTitle>
            </div>
            <div className="text-2xl sm:text-3xl font-bold text-teal-600">
              {stats.totalMeasurements}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border border-slate-100 rounded-xl shadow-sm">
          <CardContent className="pt-4 sm:pt-6 px-4 sm:px-6 pb-4 sm:pb-6">
            <div className="flex items-center gap-1.5 sm:gap-2 mb-1 sm:mb-2">
              <TrendingUp className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-red-500 flex-shrink-0" />
              <CardTitle className="text-xs sm:text-sm font-medium text-teal-600">
                Fora do normal
              </CardTitle>
            </div>
            <div className="text-2xl sm:text-3xl font-bold text-red-500">
              {stats.outOfRange}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border border-slate-100 rounded-xl shadow-sm">
          <CardContent className="pt-4 sm:pt-6 px-4 sm:px-6 pb-4 sm:pb-6">
            <div className="flex items-center gap-1.5 sm:gap-2 mb-1 sm:mb-2">
              <Activity className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-green-600 flex-shrink-0" />
              <CardTitle className="text-xs sm:text-sm font-medium text-teal-600">
                Normais
              </CardTitle>
            </div>
            <div className="text-2xl sm:text-3xl font-bold text-green-600">
              {stats.normal}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
